import { BUSINESS } from "@/lib/constants";

const CONTACT_LINE = `${BUSINESS.name}, ${BUSINESS.address}. Phone / WhatsApp: ${BUSINESS.phoneDisplay}. Email: ${BUSINESS.email}.`;

export const LEGAL_DOCS = {
  "privacy-policy": {
    slug: "privacy-policy",
    title: "Privacy Policy",
    metaTitle: "Privacy Policy | Nandanaaya Spices",
    metaDescription:
      "How Nandanaaya Spices collects, uses and protects the information submitted through the wholesale enquiry form.",
    intro:
      "This Privacy Policy explains what information Nandanaaya Spices collects through this website, why it is collected and how it is handled. It applies only to this website and to enquiries received through it.",
    sections: [
      {
        heading: "1. Information we collect",
        body: [
          "Through the wholesale enquiry form we collect the contact name, business name, mobile or WhatsApp number, city, product required, approximate quantity and requirement details you submit. You may optionally provide an email address, grade or specification, expected purchase date and GST invoice preference.",
          "Basic technical information such as the submission time is recorded with each enquiry so that we can respond in order of receipt. We do not knowingly collect information from persons who are not enquiring on behalf of a business.",
        ],
      },
      {
        heading: "2. Why we use it",
        body: [
          "The information is used only to respond to your enquiry: to confirm current lot availability, grade, quantity, quotation and delivery arrangements, and to maintain a record of commercial correspondence.",
          "We may contact you by phone, WhatsApp or email using the details you provide. You can ask us to stop contacting you at any time.",
        ],
      },
      {
        heading: "3. What we do not do",
        body: [
          "We do not sell, rent or trade enquiry information to any third party.",
          "We do not collect card details, bank credentials or payment information through this website. This website does not process online payments.",
          "We never ask for passwords, OTPs or card numbers by email or WhatsApp.",
        ],
      },      {
        heading: "4. Storage and retention",
        body: [
          "Enquiries are stored in a private database accessible only to the proprietor. Enquiry records are retained for as long as required for commercial correspondence, statutory record-keeping and legitimate business purposes, after which they may be deleted.",
          "We take reasonable care to protect enquiry information, including restricted access to the enquiry records. No method of transmission or storage can be guaranteed completely secure.",
        ],
      },
      {
        heading: "5. Service providers",
        body: [
          "We use third-party hosting and email-delivery services to operate this website and to deliver enquiry notifications to our business inbox. These providers process the information only for the purpose of delivering that service.",
        ],
      },
      {
        heading: "6. Your requests",
        body: [
          "You may request a copy of the enquiry information we hold about you, ask for it to be corrected, or ask for it to be deleted, by contacting us using the details below. We will respond within a reasonable period.",
        ],
      },
      {
        heading: "7. Contact",
        body: [CONTACT_LINE],
      },
    ],
  },

  "terms-and-conditions": {
    slug: "terms-and-conditions",
    title: "Terms & Conditions",
    metaTitle: "Terms & Conditions | Nandanaaya Spices",
    metaDescription:
      "Terms governing quotations, orders, grades, payment and supply of wholesale spices by Nandanaaya Spices, Hyderabad.",
    intro:
      "These terms apply to the use of this website and to wholesale enquiries, quotations and orders handled by Nandanaaya Spices, a sole proprietorship based in Hyderabad, Telangana.",
    sections: [
      {
        heading: "1. Nature of the website",
        body: [
          "This website is an information and enquiry platform for business buyers. It is not an online store. No prices are published, no cart or checkout is provided, and no payment is collected through this website.",
          "Submitting an enquiry does not create a confirmed order or any commitment to supply. An order arises only after a written quotation is issued by us and accepted by the buyer, and payment terms are agreed.",
          "Prices, grades and availability depend entirely on the physical lot in stock at the time of enquiry.",
        ],
      },
      {
        heading: "2. Quotations",
        body: [
          "Quotations are issued against the lot physically available at the time of enquiry and prevailing market conditions. Every quotation states its own validity period; unless stated otherwise, a quotation is valid only for the period mentioned in it.",
          "Once a quotation expires, or the referenced lot is exhausted, the price and availability must be reconfirmed.",
        ],
      },
      {
        heading: "3. Grades, samples and specification",
        body: [
          "Size, colour and grade descriptions used on this website and in quotations are indicative. Final supply is based on the actual available lot, the sample or lot photograph approved by the buyer, and the written specification confirmed in the quotation.",
          "Buyers are encouraged to approve an actual-lot sample or lot photograph before confirming an order. Natural agricultural produce varies between lots and seasons.",
        ],
      },
      {
        heading: "4. Orders and payment",
        body: [
          "Orders are accepted against advance payment or payment confirmed before dispatch, unless separately agreed in writing. Credit is not automatically available.",
          "Dispatch begins only after payment clearance is confirmed in our account and order documentation is complete.",
          "Freight, delivery, inspection, cancellation, shortage, damage and refund conditions for any order follow the accepted written quotation for that order, read together with the policies on this website.",
          "GST invoices are issued for orders as per applicable law using the registration details published on this website.",
        ],
      },
      {
        heading: "5. Service area",
        body: [
          "Our primary market is Hyderabad and Telangana. Orders for other locations in India are considered subject to quantity, logistics, payment confirmation and commercial feasibility.",
        ],
      },
      {
        heading: "6. Website content",
        body: [
          "Product photographs on this website are representative unless specifically labelled otherwise. A representative photograph does not guarantee the appearance, size, colour or condition of the current lot.",
          "Content on this website is provided for general commercial information. We may update products, descriptions and policies without prior notice.",
        ],
      },
      {
        heading: "7. Liability",
        body: [
          "Our liability in respect of any supply is limited to the invoice value of the goods concerned. We are not liable for indirect or consequential loss, including loss of profit or business interruption.",
        ],
      },
      {
        heading: "8. Governing law",
        body: [
          "These terms are governed by the laws of India. Courts at Hyderabad, Telangana have exclusive jurisdiction over any dispute.",
        ],
      },
      {
        heading: "9. Contact",
        body: [CONTACT_LINE],
      },
    ],
  },

  "shipping-and-delivery": {
    slug: "shipping-and-delivery",
    title: "Shipping & Delivery Policy",
    metaTitle: "Shipping & Delivery Policy | Nandanaaya Spices",
    metaDescription:
      "How wholesale spice orders are packed, dispatched and delivered by Nandanaaya Spices from Hyderabad.",
    intro:
      "This policy explains how confirmed wholesale orders are packed, dispatched and delivered. Dispatch timelines are commercial estimates and depend on lot availability, payment clearance and transport arrangements.",
    sections: [
      {
        heading: "1. Dispatch after payment clearance",
        body: [
          "Goods are weighed, packed and prepared for dispatch after the agreed payment is confirmed as cleared and the order specification is finalised. Credit is not automatically available.",
          "Freight, delivery, inspection, shortage and damage conditions for each consignment follow the accepted written quotation for that order.",
          "Where a sample or lot photograph approval is part of the order, dispatch follows that approval.",
        ],
      },
      {
        heading: "2. Packing",
        body: [
          "Material is packed in suitable wholesale packaging for the product and quantity, weighed before sealing, and handed over to the transporter or courier agreed with the buyer.",
        ],
      },
      {
        heading: "3. Delivery within Hyderabad and Telangana",
        body: [
          "For buyers in Hyderabad and elsewhere in Telangana, delivery or transport handover is arranged as agreed at the time of order confirmation.",
        ],
      },
      {
        heading: "4. Outstation consignments",
        body: [
          "Orders for other locations in India are accepted subject to quantity, logistics, payment confirmation and commercial feasibility, and are normally sent through a transporter or courier selected for the consignment.",
        ],
      },
      {
        heading: "5. Freight and risk",
        body: [
          "Unless expressly included in the quotation, freight, loading, unloading, octroi or similar charges are payable by the buyer.",
          "Once goods are handed over to the transporter or courier nominated or accepted by the buyer, transit risk passes to the buyer. We will provide the lorry receipt or docket details to help the buyer track and claim.",
        ],
      },
      {
        heading: "6. Receiving the consignment",
        body: [
          "Buyers should check the number of packages, seals and weight at the time of delivery and record any shortage or visible damage on the transporter's delivery document before accepting the consignment.",
        ],
      },
      {
        heading: "7. Contact",
        body: [CONTACT_LINE],
      },
    ],
  },

  "cancellation-and-refund": {
    slug: "cancellation-and-refund",
    title: "Cancellation & Refund Policy",
    metaTitle: "Cancellation & Refund Policy | Nandanaaya Spices",
    metaDescription:
      "Cancellation, shortage, transit damage and quality-variance claim process for wholesale spice orders from Nandanaaya Spices.",
    intro:
      "This policy applies to confirmed wholesale orders. Because spices are procured lot-wise against buyer-approved specification, cancellation is possible only within the limits described below.",
    sections: [
      {
        heading: "1. Cancellation before procurement",
        body: [
          "An order may be cancelled without deduction if procurement, segregation or packing for that order has not yet commenced. Any amount already received is refunded.",
        ],
      },
      {
        heading: "2. Cancellation after procurement",
        body: [
          "If material has already been procured, segregated or packed specifically for the order, cancellation is accepted only by mutual agreement. Actual costs incurred, including procurement difference, handling and packing, may be deducted.",
        ],
      },
      {
        heading: "3. After dispatch",
        body: [
          "Once a consignment has been dispatched, it cannot be cancelled. Any return requires prior written agreement and is subject to freight and handling recovery.",
        ],
      },
      {
        heading: "4. Shortage or transit damage",
        body: [
          "Shortage, tampering or transit damage must be recorded on the transporter's delivery document at the time of receipt and reported to us within 48 hours of delivery, with photographs of the packages and weighment proof.",
          "We will support the buyer in pursuing the claim with the transporter. Liability for transit loss rests with the transporter once the consignment has been handed over.",
        ],
      },
      {
        heading: "5. Quality variance",
        body: [
          "Any claim that the supplied material differs from the approved sample or confirmed written specification must be raised within 48 hours of delivery, before the material is processed, mixed, repacked or resold, with photographs and the retained sample available for comparison.",
          "Where a variance from the confirmed specification is established, we will offer a replacement from an available lot, a price adjustment or a refund of the affected portion, as mutually agreed.",
          "Natural variation in colour, size and aroma between agricultural lots and seasons is not treated as a defect.",
        ],
      },
      {
        heading: "6. Refund method",
        body: [
          "Approved refunds are made to the same bank account from which the payment was received, normally within 7 to 10 working days of agreement, after adjusting any charges stated above.",
        ],
      },
      {
        heading: "7. Contact",
        body: [CONTACT_LINE],
      },
    ],
  },

  disclaimer: {
    slug: "disclaimer",
    title: "Disclaimer",
    metaTitle: "Disclaimer | Nandanaaya Spices",
    metaDescription:
      "Disclaimer covering product photographs, indicative grades, pricing and information published on the Nandanaaya Spices website.",
    intro:
      "The information on this website is published for general commercial reference by business buyers.",
    sections: [
      {
        heading: "1. Photographs",
        body: [
          "Product and lot photographs are representative unless specifically labelled otherwise. They illustrate the type of material we trade and do not guarantee the appearance, size, colour or condition of the currently available physical lot.",
        ],
      },
      {
        heading: "2. Grades and specifications",
        body: [
          "Size and grade descriptions, including millimetre ranges for cardamom, are indicative commercial categories. Final supply is based on the actual available lot, the approved sample and the written commercial specification.",
        ],
      },
      {
        heading: "3. Prices and availability",
        body: [
          "No prices are published on this website. Spice prices and availability change with market conditions and with each lot. All rates are quoted on enquiry and remain valid only for the period stated in the quotation.",
        ],
      },
      {
        heading: "4. No product or health claims",
        body: [
          "Nothing on this website should be read as a medicinal, nutritional or therapeutic claim. We do not publish laboratory results, certifications or test reports unless they are provided with the specific lot concerned.",
        ],
      },
      {
        heading: "5. Registration details",
        body: [
          "Statutory registration numbers are published as recorded in our business documents. Buyers may verify them independently on the relevant government portals.",
        ],
      },
      {
        heading: "6. External links",
        body: [
          "This website may link to external services such as WhatsApp. We are not responsible for the content or policies of external services.",
        ],
      },
      {
        heading: "7. Contact",
        body: [CONTACT_LINE],
      },
    ],
  },
};
